/**
 * Fixed-step adapter between an authored worker route and stealth's Watcher.
 *
 * A patrol is deliberately not another AI. `Worker` remains the sole owner of
 * movement, while this object keeps stealth's plain-data view in lockstep with
 * the fixed simulation that produced it.
 */

import { Worker, type WorkerRoute, type WorkerState } from '@/systems/worker';
import type { Watcher } from '@/systems/stealth';

/** The authored stealth properties carried by one worker route. */
export interface PatrolDefinition {
  id: string;
  route: WorkerRoute;
  coneHalfAngle: number;
  range: number;
  eyeHeight: number;
}

function finite(value: number, field: string): void {
  if (!Number.isFinite(value)) throw new Error(`patrol ${field} must be finite`);
}

/**
 * Makes a route-following worker observable to `StealthSystem` without giving
 * either system scene, physics, or clock dependencies.
 */
export class Patrol {
  readonly #worker: Worker;
  readonly #watcher: Watcher;

  constructor(definition: PatrolDefinition) {
    if (definition.id.trim().length === 0) throw new Error('patrol id must not be empty');
    finite(definition.coneHalfAngle, 'coneHalfAngle');
    if (definition.coneHalfAngle <= 0 || definition.coneHalfAngle > Math.PI) {
      throw new Error('patrol coneHalfAngle must be in (0, Math.PI]');
    }
    finite(definition.range, 'range');
    if (definition.range <= 0) throw new Error('patrol range must be positive');
    finite(definition.eyeHeight, 'eyeHeight');
    if (definition.eyeHeight < 0) throw new Error('patrol eyeHeight must not be negative');

    // `Worker` owns route validation so this adapter cannot let the two route
    // contracts drift apart as route content gains new authored fields.
    this.#worker = new Worker(definition.route);
    const state = this.#worker.state;
    this.#watcher = {
      id: definition.id,
      // The floor is deliberately zero: evaluateVisibility adds eyeHeight when
      // casting, and storing it here too would put a 1.6 m worker's eyes above
      // a 2.6 m corridor.
      position: { x: state.x, y: 0, z: state.z },
      // This is checked against Worker in patrol.spec.ts rather than assumed:
      // both contracts use 0 toward +Z, increasing toward +X.
      facing: state.facing,
      coneHalfAngle: definition.coneHalfAngle,
      range: definition.range,
      eyeHeight: definition.eyeHeight,
    };
  }

  get id(): string {
    return this.#watcher.id;
  }

  get state(): Readonly<WorkerState> {
    return this.#worker.state;
  }

  get watcher(): Watcher {
    // StealthSystem keeps the Watcher reference supplied at construction and
    // rereads it every fixed step. Returning a fresh value here would freeze its
    // cone at spawn while the visible worker walks away: an unexplainable and
    // therefore unfair detection under spec §5.1.
    return this.#watcher;
  }

  start(): void {
    this.#worker.start();
  }

  interrupt(): void {
    this.#worker.interrupt();
  }

  /** Send a parked worker straight back out on a fresh round. See `Worker.restart`. */
  restart(): void {
    this.#worker.restart();
  }

  /** Advance movement once, then expose precisely that fixed-step result to stealth. */
  step(dt: number): Readonly<WorkerState> {
    const state = this.#worker.step(dt);
    this.#watcher.position.x = state.x;
    // Floor position, not eye position: `evaluateVisibility` owns that offset.
    this.#watcher.position.y = 0;
    this.#watcher.position.z = state.z;
    this.#watcher.facing = state.facing;
    return state;
  }
}
